import PubSub from "pubsub-js"
import Typography from "aldoo-ra/Typography"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import Image from "aldoo-ra/Image"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import {
  GlobeAltIcon,
  EyeIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  ClockIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  CloudArrowUpIcon,
  CheckIcon as Check,
  VideoCameraIcon,
  CameraIcon,
  MicrophoneIcon,
  RectangleGroupIcon,
  ComputerDesktopIcon,
  WindowIcon,
  RectangleStackIcon,
  Square3Stack3DIcon,
  BoltIcon,
  SwatchIcon,
  EyeDropperIcon,
  LinkIcon,
  EyeSlashIcon,
  Squares2X2Icon,
  FaceSmileIcon,
  ArrowsRightLeftIcon,
  ArrowsUpDownIcon,
  ViewColumnsIcon,
} from "@heroicons/react/24/outline"
import MainSection from "./MainSection"
import FeaturesCarousel from "./FeaturesCarousel"

// ─── Mockups for the 4 main sections ───────────────────────────────────────────

function Shell({ title, accent = "#3b82f6", headerIcon: HeaderIcon, children }) {
  return (
    <div className="relative w-full max-w-[560px] mx-auto select-none">
      <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full blur-3xl pointer-events-none"
        style={{ background: `${accent}26` }} />
      <div className="relative rounded-2xl border border-white/10"
        style={{ background: "#0d1117", boxShadow: "0 32px 64px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)" }}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07] rounded-t-2xl"
          style={{ background: "rgba(255,255,255,0.03)" }}>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
          </div>
          <span className="flex items-center gap-2 text-gray-200 text-sm font-medium tracking-tight">
            {HeaderIcon && <HeaderIcon className="w-4 h-4" style={{ color: accent }} />}
            {title}
          </span>
          <div className="w-8" />
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  )
}

// Data-driven mockup: renders the section's actual bullets as feature rows,
// each with its own accent icon. Sized to fill the text column.
function FeatureMockup({ title, accent, headerIcon, rows }) {
  return (
    <Shell title={title} accent={accent} headerIcon={headerIcon}>
      <div className="flex flex-col gap-3">
        {rows.map(({ Icon, label }, i) => (
          <div key={i} className="flex items-center gap-4 rounded-xl border border-white/10 px-4 py-4"
            style={{ background: "rgba(255,255,255,0.03)" }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${accent}1f` }}>
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </span>
            <span className="text-sm text-gray-200 leading-snug flex-1">{label}</span>
            <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: accent }}>
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </span>
          </div>
        ))}
      </div>
    </Shell>
  )
}

// CMS-image mockup — the image already has a dark, near-black background,
// so it drops into the same glow + tilt treatment as the app-shell mockups.
function ImageMockup({ accent = "#3b82f6" }) {
  return (
    <div className="relative w-full max-w-[560px] mx-auto select-none">
      <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full blur-3xl pointer-events-none"
        style={{ background: `${accent}26` }} />
      <div className="relative rounded-2xl overflow-hidden border border-white/10"
        style={{ boxShadow: "0 32px 64px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)" }}>
        <Image id="explainer_video" className="w-full h-auto block" />
      </div>
    </div>
  )
}

// Built inside the component (not module-level) since badge/title/description/
// bullets come from the CMS texts bundle.
function getSections(texts) {
  return [
    {
      accent: "#06b6d4",
      headerIcon: RectangleGroupIcon,
      rowIcons: [RectangleGroupIcon, ComputerDesktopIcon, WindowIcon],
      badge: texts?.section_1_badge,
      title: texts?.section_1_title,
      description: texts?.section_1_description,
      bullets: [texts?.section_1_bullet_1, texts?.section_1_bullet_2, texts?.section_1_bullet_3].filter(Boolean),
    },
    {
      accent: "#10b981",
      headerIcon: RectangleStackIcon,
      rowIcons: [RectangleStackIcon, ArrowPathIcon, DocumentTextIcon],
      badge: texts?.section_2_badge,
      title: texts?.section_2_title,
      description: texts?.section_2_description,
      bullets: [texts?.section_2_bullet_1, texts?.section_2_bullet_2, texts?.section_2_bullet_3].filter(Boolean),
    },
    {
      accent: "#3b82f6",
      headerIcon: VideoCameraIcon,
      rowIcons: [VideoCameraIcon, CameraIcon, MicrophoneIcon],
      badge: texts?.section_3_badge,
      title: texts?.section_3_title,
      description: texts?.section_3_description,
      bullets: [texts?.section_3_bullet_1, texts?.section_3_bullet_2, texts?.section_3_bullet_3].filter(Boolean),
    },
    {
      accent: "#f59e0b",
      headerIcon: CameraIcon,
      rowIcons: [ClockIcon, Square3Stack3DIcon, PencilSquareIcon],
      badge: texts?.section_4_badge,
      title: texts?.section_4_title,
      description: texts?.section_4_description,
      bullets: [texts?.section_4_bullet_1, texts?.section_4_bullet_2, texts?.section_4_bullet_3].filter(Boolean),
    },
    {
      accent: "#ec4899",
      customMockup: ImageMockup,
      badge: texts?.section_5_badge,
      title: texts?.section_5_title,
      description: texts?.section_5_description,
      bullets: [texts?.section_5_bullet_1, texts?.section_5_bullet_2].filter(Boolean),
    },
    {
      accent: "#8b5cf6",
      headerIcon: PencilSquareIcon,
      rowIcons: [PencilSquareIcon, DocumentTextIcon, EyeIcon],
      badge: texts?.section_6_badge,
      title: texts?.section_6_title,
      description: texts?.section_6_description,
      bullets: [texts?.section_6_bullet_1, texts?.section_6_bullet_2, texts?.section_6_bullet_3].filter(Boolean),
    },
    {
      accent: "#64748b",
      headerIcon: EyeSlashIcon,
      rowIcons: [EyeSlashIcon, Squares2X2Icon, FaceSmileIcon],
      badge: texts?.section_7_badge,
      title: texts?.section_7_title,
      description: texts?.section_7_description,
      bullets: [texts?.section_7_bullet_1, texts?.section_7_bullet_2, texts?.section_7_bullet_3].filter(Boolean),
    },
    {
      accent: "#0ea5e9",
      headerIcon: ArrowsRightLeftIcon,
      rowIcons: [ViewColumnsIcon, ArrowsUpDownIcon, ArrowsRightLeftIcon],
      badge: texts?.section_8_badge,
      title: texts?.section_8_title,
      description: texts?.section_8_description,
      bullets: [texts?.section_8_bullet_1, texts?.section_8_bullet_2, texts?.section_8_bullet_3].filter(Boolean),
    },
    {
      accent: "#f43f5e",
      headerIcon: SwatchIcon,
      rowIcons: [SwatchIcon, EyeDropperIcon, LinkIcon],
      badge: texts?.section_9_badge,
      title: texts?.section_9_title,
      description: texts?.section_9_description,
      bullets: [texts?.section_9_bullet_1, texts?.section_9_bullet_2, texts?.section_9_bullet_3].filter(Boolean),
    },
    {
      accent: "#22c55e",
      headerIcon: GlobeAltIcon,
      rowIcons: [CloudArrowUpIcon, GlobeAltIcon, ArrowUpTrayIcon],
      badge: texts?.section_10_badge,
      title: texts?.section_10_title,
      description: texts?.section_10_description,
      bullets: [texts?.section_10_bullet_1, texts?.section_10_bullet_2, texts?.section_10_bullet_3].filter(Boolean),
    },
  ]
}

function getCards(texts) {
  return [
    { icon: RectangleGroupIcon, color: "#3b82f6", title: texts?.card_1_title, description: texts?.card_1_description },
    { icon: WindowIcon, color: "#8b5cf6", title: texts?.card_2_title, description: texts?.card_2_description },
    { icon: ComputerDesktopIcon, color: "#10b981", title: texts?.card_3_title, description: texts?.card_3_description },
    { icon: RectangleStackIcon, color: "#ec4899", title: texts?.card_4_title, description: texts?.card_4_description },
    { icon: CameraIcon, color: "#f59e0b", title: texts?.card_5_title, description: texts?.card_5_description },
    { icon: VideoCameraIcon, color: "#06b6d4", title: texts?.card_6_title, description: texts?.card_6_description },
    { icon: MicrophoneIcon, color: "#64748b", title: texts?.card_7_title, description: texts?.card_7_description },
    { icon: GlobeAltIcon, color: "#22c55e", title: texts?.card_8_title, description: texts?.card_8_description },
    { icon: PencilSquareIcon, color: "#0ea5e9", title: texts?.card_9_title, description: texts?.card_9_description },
    { icon: SwatchIcon, color: "#f43f5e", title: texts?.card_10_title, description: texts?.card_10_description },
    { icon: EyeSlashIcon, color: "#a855f7", title: texts?.card_11_title, description: texts?.card_11_description },
    { icon: ArrowsRightLeftIcon, color: "#eab308", title: texts?.card_12_title, description: texts?.card_12_description },
    { icon: BoltIcon, color: "#ef4444", title: texts?.card_13_title, description: texts?.card_13_description },
  ]
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function FeaturesPage() {
  const texts = TextBundle({ id: "features_page" })
  const [auth] = PlatformAuth.useAuth()

  function onGetAldooSnap() {
    if (auth?.auth_token) {
      navigate("/account/downloads")
    } else {
      PubSub.publish("signin", { redirect: "/account/downloads" })
    }
  }

  const sections = getSections(texts)
  const cards = getCards(texts)

  return (
    <div className="relative overflow-x-clip bg-paper min-h-screen">
      {/* Hero + MainSections share one continuous gradient background */}
      <div className="relative md:pb-16">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-secondary/15 to-secondary/30 rounded-b-[70px] xl:rounded-b-[100px] pointer-events-none" />
        <section className="relative  text-center md:-mt-16 pt-28 md:pt-44  px-6">
          <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center">
            <Typography
              component="h1"
              variant="h1"
              className="text-text mt-0 mb-5"
            >
              {texts?.hero_title}
            </Typography>

            <Typography
              variant="body1_dim"
              className="mb-8 lg:mb-10 mt-0"
            >
              {texts?.hero_subtitle}
            </Typography>

            <Button
              variant="primary_l"
              onClick={onGetAldooSnap}
            >
              {texts?.hero_cta}
            </Button>
          </div>

          <Typography
            variant="txt1_dim"
            className="my-0"
          >
            {texts?.hero_download_note}
          </Typography>

        </section>

        {/* Main alternating sections */}
        {sections.map((s, i) => (
          <MainSection
            key={i}
            reverse={i % 2 === 1}
            badge={s.badge}
            title={s.title}
            description={s.description}
            bullets={s.bullets}
            mockup={
              s.customMockup
                ? <s.customMockup accent={s.accent} />
                : (
                  <FeatureMockup
                    title={s.title}
                    accent={s.accent}
                    headerIcon={s.headerIcon}
                    rows={s.bullets
                      .filter(Boolean)
                      .map((label, j) => ({ Icon: s.rowIcons[j] || s.headerIcon, label }))}
                  />
                )
            }
          />
        ))}
      </div>

      {/* Carousel */}
      <section className="relative pt-24 pb-8" style={{ overflowX: "clip" }}>
        <Typography
          component="h2"
          variant="h1"
          className="text-center mx-8 mb-2"
        >
          {texts?.carousel_title}
        </Typography>

        <div className="flex items-center justify-center gap-1 text-center mb-2">
          <Typography variant="body1_dim">
            {texts?.carousel_subtitle}
          </Typography>

          <ChevronRightIcon className="w-4 h-4 text-text_secondary stroke-2" />
        </div>
        <FeaturesCarousel cards={cards} />
      </section>
    </div>
  )
}
